/* import __COLOCATED_TEMPLATE__ from './tags-picker-bridge.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { next } from '@ember/runloop';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import type CommandKService from 'embercom/services/command-k';
import type UserApi from 'embercom/services/user-api';
import type CompanyApi from 'embercom/services/company-api';
import type Tag from 'embercom/objects/inbox/tag';
import { CommandKTagActionId } from 'embercom/objects/inbox/command-k/tag-item';
import { ResponseError } from 'embercom/lib/inbox/requests';

type EntityType = 'user' | 'lead' | 'company';

interface ActiveRequest {
  requestId: string;
  entityType: EntityType;
  entityId: string;
}

interface ActiveContext {
  request: ActiveRequest;
  entity: unknown;
  tags: Tag[];
}

interface PermissionsService {
  loadAllAdminsAndShowPermissionRequestModal(permission: string): void;
}

const OPEN_PICKER_EVENT = 'tags:open-picker';
const PICKED_EVENT = 'tags:picker-selected';
const CLOSED_EVENT = 'tags:picker-closed';

interface Signature {
  Args: {};
}

/**
 * Renders the Ember CommandK tag picker on behalf of the React TagsSection components.
 *
 * The React side (`apps/teammate-app/src/features/conversation-details-sidebar/hooks/use-ember-tag-picker.ts`)
 * dispatches `tags:open-picker` with `{ requestId, entityType, entityId }`. This component
 * subscribes, fetches the entity + current tags, registers a CommandK action, and triggers
 * `findAndShow`. Selections and dismiss are reported back via `tags:picker-selected` and
 * `tags:picker-closed` so React can apply the picked tag through its own mutation hooks.
 *
 * Mount this once near the React island that hosts the tag sections — it is a singleton
 * relative to the conversation page.
 */
export default class TagsPickerBridge extends Component<Signature> {
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare commandK: CommandKService;
  @service declare userApi: UserApi;
  @service declare companyApi: CompanyApi;
  @service declare permissionsService: PermissionsService;

  @tracked activeContext: ActiveContext | null = null;

  private hasDispatchedSelected = false;
  private unsubscribe?: () => void;
  // Generation token for the most recent open request. Async paths (fetch
  // completion, deferred findAndShow) check `this.currentRequestId === request.requestId`
  // before committing state — if a newer request has arrived, the older path
  // bails out silently and the newer request has already dispatched CLOSED for it.
  private currentRequestId: string | null = null;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.unsubscribe = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      OPEN_PICKER_EVENT,
      (data: Record<string, unknown>) => {
        let { requestId, entityType, entityId } = data;
        if (
          typeof requestId !== 'string' ||
          typeof entityId !== 'string' ||
          (entityType !== 'user' && entityType !== 'lead' && entityType !== 'company')
        ) {
          return;
        }
        this.handleOpenRequest({ requestId, entityType, entityId });
      },
    );
  }

  willDestroy() {
    super.willDestroy();
    this.unsubscribe?.();
  }

  private async handleOpenRequest(request: ActiveRequest): Promise<void> {
    // Supersede any in-flight or active request: dispatch CLOSED so React can
    // release its pending requestId, then mark this request as the current one.
    // The previous request's async path will bail out via the generation check
    // after its next await (or before findAndShow runs).
    if (this.currentRequestId !== null && this.currentRequestId !== request.requestId) {
      this.reactTeammateAppIsland.dispatchTeammateAppEvent(CLOSED_EVENT, {
        requestId: this.currentRequestId,
      });
    }
    this.currentRequestId = request.requestId;
    this.hasDispatchedSelected = false;

    try {
      let entity: unknown;
      let tags: Tag[];
      if (request.entityType === 'company') {
        entity = await this.companyApi.fetchCompany(request.entityId);
        tags = await this.companyApi.fetchCompanyTags(request.entityId);
      } else {
        entity = await this.userApi.fetchUser(request.entityId);
        tags = await this.userApi.fetchUserTags(request.entityId);
      }
      // A newer request arrived during the fetch. It has already dispatched
      // CLOSED for our requestId — abandon silently to avoid clobbering its state.
      if (this.currentRequestId !== request.requestId) {
        return;
      }
      this.activeContext = { request, entity, tags };
      // Defer to next runloop so the <Inbox2::CommandK::Action> registers in the
      // template before commandK.findAndShow looks it up by id. Setting activeContext
      // triggers a re-render that mounts the action; findAndShow then resolves it.
      next(this, () => {
        if (this.currentRequestId !== request.requestId) {
          return;
        }
        try {
          this.commandK.findAndShow(this.actionId, this.handleClose);
        } catch (deferredError: unknown) {
          // findAndShow can throw synchronously (e.g. if the action isn't yet
          // in commandK.activeActions). Because we're inside a deferred runloop
          // frame, the throw cannot reach the outer try/catch — release the
          // requestId here so React's pending mutation doesn't hang forever.
          this.reactTeammateAppIsland.dispatchTeammateAppEvent(CLOSED_EVENT, {
            requestId: request.requestId,
          });
          this.activeContext = null;
          if (this.currentRequestId === request.requestId) {
            this.currentRequestId = null;
          }
          // Rethrow so Ember's runloop error handler still surfaces the bug.
          throw deferredError;
        }
      });
    } catch (e: unknown) {
      // If a newer request superseded us mid-fetch, it has already dispatched
      // CLOSED for our requestId — don't double-dispatch.
      if (this.currentRequestId !== request.requestId) {
        return;
      }
      this.reactTeammateAppIsland.dispatchTeammateAppEvent(CLOSED_EVENT, {
        requestId: request.requestId,
      });
      this.activeContext = null;
      this.currentRequestId = null;
      if (e instanceof ResponseError && e.response.status === 403) {
        let body = await e.response
          .clone()
          .json()
          .catch(() => null);
        let permissionRequired = body?.required_permission;
        if (typeof permissionRequired === 'string') {
          this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(permissionRequired);
        }
      }
    }
  }

  get actionId(): string {
    if (!this.activeContext) {
      return CommandKTagActionId.TagUser;
    }
    switch (this.activeContext.request.entityType) {
      case 'lead':
        return CommandKTagActionId.TagLead;
      case 'company':
        return CommandKTagActionId.TagCompany;
      case 'user':
      default:
        return CommandKTagActionId.TagUser;
    }
  }

  get pickerContext(): Record<string, unknown> | null {
    if (!this.activeContext) {
      return null;
    }
    if (this.activeContext.request.entityType === 'company') {
      return { company: this.activeContext.entity, tags: this.activeContext.tags };
    }
    return { user: this.activeContext.entity, tags: this.activeContext.tags };
  }

  @action handleSelect(selectedTag: Tag): void {
    if (!this.activeContext) {
      return;
    }
    this.hasDispatchedSelected = true;
    this.reactTeammateAppIsland.dispatchTeammateAppEvent(PICKED_EVENT, {
      requestId: this.activeContext.request.requestId,
      picked: { id: selectedTag.id, name: selectedTag.name },
    });
  }

  @action handleClose(): void {
    if (!this.activeContext) {
      return;
    }
    if (!this.hasDispatchedSelected) {
      this.reactTeammateAppIsland.dispatchTeammateAppEvent(CLOSED_EVENT, {
        requestId: this.activeContext.request.requestId,
      });
    }
    if (this.currentRequestId === this.activeContext.request.requestId) {
      this.currentRequestId = null;
    }
    this.activeContext = null;
    this.hasDispatchedSelected = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Bridge::TagsPickerBridge': typeof TagsPickerBridge;
    'inbox2/bridge/tags-picker-bridge': typeof TagsPickerBridge;
  }
}
